/**
 * Urgent "a study load has begun" signal, outside React Router's transition.
 *
 * React Router v7 wraps navigation state in startTransition, so after a
 * worklist click React keeps the OLD study on screen until the new tree has
 * fully rendered — and that commit also carries the old study's teardown.
 * The loading overlay lives in that deferred tree, so it appeared only after
 * the very delay it was meant to cover.
 *
 * This tiny external store bypasses that: the click handler flags the target
 * study synchronously (flushSync at the call site), the overlay subscribes
 * here and paints the loader in the same frame as the click, and the router
 * transition + teardown then happen behind it. StudyDetail clears the flag
 * once the target study is revealed (or errors).
 */

import { useSyncExternalStore } from 'react';

export interface PendingStudyLoad {
  uuid: string;
  /** Patient display name, when the gesture source already had it (the
   *  worklist row does) — lets the breadcrumb update in the same frame as
   *  the click instead of waiting for the study fetch. */
  patientName: string | null;
}

let pending: PendingStudyLoad | null = null;
const listeners = new Set<() => void>();

function emit() {
  listeners.forEach((l) => l());
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function beginStudyLoadGesture(uuid: string, patientName?: string) {
  if (pending?.uuid === uuid) return;
  pending = { uuid, patientName: patientName ?? null };
  emit();
}

export function clearStudyLoadGesture() {
  if (pending === null) return;
  pending = null;
  emit();
}

export function usePendingStudyLoad(): PendingStudyLoad | null {
  return useSyncExternalStore(subscribe, () => pending);
}
